import { useState } from 'react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { HelpCircle } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

// --- Mock Data specific to the 3 scenarios ---
const MOCK_BUILDINGS = [
  {
    id: 'hangar-a',
    name: 'Hangar A',
    total_sqft: 25000,
    occupancy_pct: 95,
    revenue: 18000,
    fuel_revenue: 14000,
    total_value: 32000,
    yield: 0.72,
    market_avg: 1.10,
    segments: {
        lease: { pct: 85, rev: 16000 },
        transient: { pct: 10, rev: 2000 },
        idle: { pct: 5, rev: 0 }
    },
    clients: [
        { name: 'Corp Flight Dept', pct: 60, rev: 10000, fuel_rev: 12000 },
        { name: 'Private Owner', pct: 25, rev: 6000, fuel_rev: 1500 },
        { name: 'Transient', pct: 10, rev: 2000, fuel_rev: 500 }
    ],
    note: "High space revenue, high occupancy, but underpriced. Yield/sqft reveals it's the worst deal per space, but fuel volume saves the total relationship value!"
  },
  {
    id: 'hangar-b',
    name: 'Hangar B',
    total_sqft: 6956,
    occupancy_pct: 52,
    revenue: 8000,
    fuel_revenue: 1000,
    total_value: 9000,
    yield: 1.15,
    market_avg: 1.05,
    segments: {
        lease: { pct: 30, rev: 5000 },
        transient: { pct: 22, rev: 3000 },
        idle: { pct: 48, rev: 0 }
    },
    clients: [
        { name: 'Charter Operator', pct: 30, rev: 5000, fuel_rev: 1000 },
        { name: 'Various Transients', pct: 22, rev: 3000, fuel_rev: 0 }
    ],
    note: "Low revenue, low occupancy, but great space pricing. However, they buy almost no fuel."
  },
  {
    id: 'tiedowns',
    name: 'North Tiedowns',
    total_sqft: 6666,
    occupancy_pct: 71,
    revenue: 3000,
    fuel_revenue: 1500,
    total_value: 4500,
    yield: 0.45,
    market_avg: 0.35,
    segments: {
        lease: { pct: 50, rev: 2300 },
        transient: { pct: 21, rev: 700 },
        idle: { pct: 29, rev: 0 }
    },
    clients: [
        { name: 'Flight School', pct: 40, rev: 1800, fuel_rev: 1200 },
        { name: 'Local Owners', pct: 10, rev: 500, fuel_rev: 50 },
        { name: 'Transient', pct: 21, rev: 700, fuel_rev: 250 }
    ],
    note: "Space revenue looks small, but yield/sqft is strong and the tiedowns pump consistent 100LL fuel."
  }
]

// Top-level max values for scalable charts
const MAX_REV = 40000; // Increased to accommodate Total Value
const MAX_YIELD = 1.5;

// Colors
const COLOR_LEASE = 'bg-emerald-500';
const COLOR_TRANS = 'bg-blue-500';
const COLOR_FUEL = 'bg-amber-400';
const COLOR_IDLE = 'bg-slate-200';
const CLIENT_COLORS = ['bg-indigo-500', 'bg-violet-500', 'bg-fuchsia-500', 'bg-pink-500']

export const BuildingMetricsVariationsPage = () => {
    const [segmentBy, setSegmentBy] = useState<'type' | 'client'>('type')
    const [includeFuel, setIncludeFuel] = useState(false)

    const formatCur = (val: number) => `$${val.toLocaleString()}`
    const formatYld = (val: number) => `$${val.toFixed(2)}`

    // --- Helpers ---
    const renderSegments = (building: typeof MOCK_BUILDINGS[0], metricType: 'rev' | 'occ') => {
        const baseRev = includeFuel ? building.total_value : building.revenue;
        
        if (segmentBy === 'type') {
            const leaseVal = metricType === 'rev' ? building.segments.lease.rev : building.segments.lease.pct
            const transVal = metricType === 'rev' ? building.segments.transient.rev : building.segments.transient.pct
            const fuelVal = metricType === 'rev' && includeFuel ? building.fuel_revenue : 0
            const idleVal = metricType === 'rev' ? 0 : building.segments.idle.pct
            
            // For revenue, calculate width out of total building value/revenue
            const leaseW = metricType === 'rev' ? (leaseVal / baseRev) * 100 : leaseVal
            const transW = metricType === 'rev' ? (transVal / baseRev) * 100 : transVal
            const fuelW = metricType === 'rev' ? (fuelVal / baseRev) * 100 : fuelVal
            const idleW = metricType === 'rev' ? 0 : idleVal

            return (
                <div className="flex h-full w-full rounded-sm overflow-hidden bg-slate-100 text-[10px] text-white font-medium">
                    {leaseW > 0 && <div className={`${COLOR_LEASE} h-full flex items-center justify-center`} style={{ width: `${leaseW}%` }}>{leaseW > 15 ? (metricType === 'rev' ? formatCur(leaseVal) : `${leaseVal}%`) : ''}</div>}
                    {transW > 0 && <div className={`${COLOR_TRANS} h-full flex items-center justify-center`} style={{ width: `${transW}%` }}>{transW > 15 ? (metricType === 'rev' ? formatCur(transVal) : `${transVal}%`) : ''}</div>}
                    {fuelW > 0 && <div className={`${COLOR_FUEL} h-full flex items-center justify-center`} style={{ width: `${fuelW}%` }}>{fuelW > 15 ? (metricType === 'rev' ? formatCur(fuelVal) : `${fuelVal}%`) : ''}</div>}
                    {idleW > 0 && <div className={`${COLOR_IDLE} h-full flex items-center justify-center`} style={{ width: `${idleW}%` }} />}
                </div>
            )
        } else {
            // Client segmentation
            return (
                <div className="flex h-full w-full rounded-sm overflow-hidden bg-slate-100 text-[10px] text-white font-medium">
                    {building.clients.map((client, i) => {
                        const val = metricType === 'rev' 
                            ? (includeFuel ? client.rev + (client.fuel_rev || 0) : client.rev) 
                            : client.pct
                        const width = metricType === 'rev' ? (val / baseRev) * 100 : val
                        const color = CLIENT_COLORS[i % CLIENT_COLORS.length]
                        return width > 0 ? (
                            <TooltipProvider key={client.name}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className={`${color} h-full border-r border-white/20 hover:brightness-110 cursor-help transition-all`} style={{ width: `${width}%` }} />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p className="font-semibold mb-1">{client.name}</p>
                                        <p>{metricType === 'rev' ? `Value: ${formatCur(val)}` : `Space: ${val}%`} </p>
                                        {metricType === 'rev' && includeFuel && <p className="text-amber-500 font-medium text-xs mt-1">Fuel: {formatCur(client.fuel_rev || 0)}</p>}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        ) : null
                    })}
                </div>
            )
        }
    }


    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-auto">
            <PageHeader 
                title="Building Metrics Variations" 
                description="Exploring different visual approaches for Revenue, Occupancy, and Yield."
            />

            <div className="p-6 md:p-8 max-w-[1400px] mx-auto w-full space-y-12">
                
                {/* Global Controls */}
                <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm border border-slate-200 sticky top-0 z-10">
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                            <Switch 
                                id="segment-mode" 
                                checked={segmentBy === 'client'}
                                onCheckedChange={(c) => setSegmentBy(c ? 'client' : 'type')}
                            />
                            <Label htmlFor="segment-mode" className="text-sm font-medium">
                                Segment by {segmentBy === 'type' ? 'Lease/Transient' : 'Client Profile'}
                            </Label>
                        </div>
                        <div className="flex items-center gap-2 border-l border-slate-200 pl-6">
                            <Switch 
                                id="include-fuel" 
                                checked={includeFuel}
                                onCheckedChange={setIncludeFuel}
                            />
                            <Label htmlFor="include-fuel" className="text-sm font-medium">
                                Include Fuel (Total Value)
                            </Label>
                        </div>
                    </div>
                    {segmentBy === 'type' ? (
                        <div className="flex items-center gap-4 text-xs font-medium">
                            <div className="flex items-center gap-1.5"><div className={`w-3 h-3 ${COLOR_LEASE} rounded-sm`} /> Lease</div>
                            <div className="flex items-center gap-1.5"><div className={`w-3 h-3 ${COLOR_TRANS} rounded-sm`} /> Transient</div>
                            {includeFuel && <div className="flex items-center gap-1.5"><div className={`w-3 h-3 ${COLOR_FUEL} rounded-sm`} /> Fuel</div>}
                            <div className="flex items-center gap-1.5"><div className={`w-3 h-3 ${COLOR_IDLE} rounded-sm`} /> Idle</div>
                        </div>
                    ) : (
                        <div className="text-xs text-slate-500 flex items-center gap-1">
                             <HelpCircle className="w-4 h-4" /> Hover sections to see clients
                        </div>
                    )}
                </div>

                {/* Conceptual Context */}
                <Card className="bg-blue-50/50 border-blue-100">
                    <CardContent className="pt-6 text-sm text-slate-700 leading-relaxed grid md:grid-cols-3 gap-6">
                        <div>
                            <span className="font-semibold text-slate-900 block mb-1">Revenue ($)</span>
                            Answers "how much money does this building make?" A large hangar making $18K is more important to protect than a small one at $4K.
                        </div>
                        <div>
                            <span className="font-semibold text-slate-900 block mb-1">Occupancy (%)</span>
                            Answers "how much room do I have to grow?" A building at 52% is a sales opportunity. One at 95% is maxed out.
                        </div>
                        <div>
                            <span className="font-semibold text-slate-900 block mb-1">Yield / Sqft ($)</span>
                            Answers "how hard is this space working?" This efficiency metric catches underpriced leases hidden by high occupancy.
                        </div>
                    </CardContent>
                </Card>

                {/* VARIATION 1: The Analytical Grid */}
                <div className="space-y-4">
                    <div className="px-1">
                        <h2 className="text-xl font-bold tracking-tight text-slate-900">Variation 1: The Analytical Grid</h2>
                        <p className="text-sm text-slate-500">A clean, table-based approach displaying all three metrics as bullet/bar charts side-by-side.</p>
                    </div>
                    <Card>
                        <table className="w-full text-sm text-left">
                            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
                                <tr>
                                    <th className="p-4 w-[250px]">Building Context</th>
                                    <th className="p-4 w-[30%]">Revenue <span className="text-xs font-normal text-slate-400 block">Relative to max ($20K)</span></th>
                                    <th className="p-4 w-[30%]">Occupancy <span className="text-xs font-normal text-slate-400 block">Filled vs Idle space</span></th>
                                    <th className="p-4 w-[20%]">Yield / Sqft <span className="text-xs font-normal text-slate-400 block">vs. Market Avg (|)</span></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {MOCK_BUILDINGS.map(b => {
                                    const renderedRev = includeFuel ? b.total_value : b.revenue;
                                    const renderedYield = includeFuel ? (b.total_value / b.total_sqft) : b.yield;
                                    
                                    return (
                                     <tr key={b.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="p-4 align-top">
                                            <div className="font-semibold text-slate-900">{b.name}</div>
                                            <div className="text-xs text-slate-500 mt-1">{b.total_sqft.toLocaleString()} sqft total</div>
                                            <div className="text-xs text-slate-400 mt-2 italic bg-slate-50 border p-2 rounded leading-tight">
                                                "{b.note}"
                                            </div>
                                        </td>
                                        <td className="p-4 align-top">
                                            <div className="mb-1 font-medium text-slate-900">{formatCur(renderedRev)}/mo</div>
                                            <div className="h-4 w-full bg-slate-100 rounded-sm">
                                                <div className="h-full" style={{ width: `${(renderedRev / MAX_REV) * 100}%` }}>
                                                    {renderSegments(b, 'rev')}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4 align-top">
                                            <div className="mb-1 font-medium text-slate-900">{b.occupancy_pct}%</div>
                                            <div className="h-4 w-full">
                                                {renderSegments(b, 'occ')}
                                            </div>
                                        </td>
                                        <td className="p-4 align-top">
                                            <div className="flex justify-between items-end mb-1">
                                                <span className={`font-semibold ${renderedYield < b.market_avg && !includeFuel ? 'text-amber-600' : 'text-emerald-600'}`}>
                                                    {formatYld(renderedYield)}
                                                </span>
                                            </div>
                                            <div className="relative h-4 w-full bg-slate-100 rounded-sm">
                                                <div 
                                                    className={`h-full rounded-sm opacity-80 ${renderedYield < b.market_avg && !includeFuel ? 'bg-amber-400' : 'bg-emerald-400'}`} 
                                                    style={{ width: `${(renderedYield / MAX_YIELD) * 100}%` }}
                                                />
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <div 
                                                                className="absolute top-[-2px] bottom-[-2px] w-[2px] bg-slate-800 z-10 shadow-sm transition-all"
                                                                style={{ left: `${(b.market_avg / MAX_YIELD) * 100}%` }}
                                                            />
                                                        </TooltipTrigger>
                                                        <TooltipContent>Market Avg: {formatYld(b.market_avg)}</TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </div>
                                        </td>
                                    </tr>
                                )})}
                            </tbody>
                        </table>
                    </Card>
                </div>

                {/* VARIATION 2: Stacked Triple Bars */}
                <div className="space-y-4">
                    <div className="px-1">
                        <h2 className="text-xl font-bold tracking-tight text-slate-900">Variation 2: Stacked Progress Dashboard</h2>
                        <p className="text-sm text-slate-500">Each building gets a detailed horizontal breakdown. Good for deep-dives into individual buildings.</p>
                    </div>
                    <div className="grid gap-4 lg:grid-cols-3">
                        {MOCK_BUILDINGS.map(b => {
                           const renderedRev = includeFuel ? b.total_value : b.revenue;
                           const renderedYield = includeFuel ? (b.total_value / b.total_sqft) : b.yield;
                           
                           return (
                            <Card key={b.id} className="flex flex-col">
                                <CardHeader className="pb-3 bg-slate-50/50 border-b">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <CardTitle className="text-lg">{b.name}</CardTitle>
                                            <p className="text-xs text-slate-500 mt-1">{b.total_sqft.toLocaleString()} sqft total capacity</p>
                                        </div>
                                    </div>
                                    <div className="text-xs text-slate-600 italic bg-white border border-slate-100 p-2 rounded mt-3">
                                        {b.note}
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-4 space-y-6 flex-1">
                                    
                                    {/* Line 1: Revenue */}
                                    <div>
                                        <div className="flex justify-between items-baseline mb-1.5">
                                            <span className="text-sm font-medium text-slate-600">Monthly {includeFuel ? 'Value' : 'Rev.'}</span>
                                            <span className="text-base font-semibold text-slate-900">{formatCur(renderedRev)}</span>
                                        </div>
                                        <div className="h-3 w-full rounded-full overflow-hidden bg-slate-100">
                                            {renderSegments(b, 'rev')}
                                        </div>
                                        {segmentBy === 'type' && (
                                            <div className="flex justify-between text-[10px] text-slate-400 mt-1 px-1">
                                                <span>{formatCur(b.segments.lease.rev)} Lease</span>
                                                {includeFuel && <span>{formatCur(b.fuel_revenue)} Fuel</span>}
                                                <span>{formatCur(b.segments.transient.rev)} Trans.</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Line 2: Occupancy */}
                                    <div>
                                        <div className="flex justify-between items-baseline mb-1.5">
                                            <span className="text-sm font-medium text-slate-600">Space Occupied</span>
                                            <span className="text-base font-semibold text-slate-900">{b.occupancy_pct}%</span>
                                        </div>
                                        <div className="h-3 w-full rounded-full overflow-hidden bg-slate-100">
                                            {renderSegments(b, 'occ')}
                                        </div>
                                    </div>

                                    {/* Line 3: Yield */}
                                    <div>
                                        <div className="flex justify-between items-baseline mb-1.5">
                                            <span className="text-sm font-medium text-slate-600">Yield per Sqft</span>
                                            <div className="flex items-center gap-2">
                                                {renderedYield < b.market_avg && !includeFuel && (
                                                    <Badge variant="outline" className="text-amber-600 bg-amber-50 text-[10px] h-5 border-amber-200">Below Avg</Badge>
                                                )}
                                                <span className={`text-base font-bold ${renderedYield < b.market_avg && !includeFuel ? 'text-amber-600' : 'text-emerald-600'}`}>
                                                    {formatYld(renderedYield)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="relative h-3 w-full bg-slate-100 rounded-full">
                                            <div 
                                                className={`h-full rounded-full ${renderedYield < b.market_avg && !includeFuel ? 'bg-amber-400' : 'bg-emerald-400'}`} 
                                                style={{ width: `${Math.min(100, (renderedYield / MAX_YIELD) * 100)}%` }}
                                            />
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <div 
                                                            className="absolute top-[-4px] bottom-[-4px] w-1 bg-slate-800 rounded-full cursor-help hover:bg-slate-900 transition-colors"
                                                            style={{ left: `${(b.market_avg / MAX_YIELD) * 100}%` }}
                                                        />
                                                    </TooltipTrigger>
                                                    <TooltipContent>Market Average: {formatYld(b.market_avg)}</TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                    </div>

                                </CardContent>
                            </Card>
                        )})}
                    </div>
                </div>

                {/* VARIATION 3: The Sparkline List view */}
                <div className="space-y-4">
                     <div className="px-1">
                        <h2 className="text-xl font-bold tracking-tight text-slate-900">Variation 3: Unified Compact List</h2>
                        <p className="text-sm text-slate-500">Focuses on high data density. Ideal as a replacement for the existing building bars component.</p>
                    </div>
                    <Card className="divide-y divide-slate-100">
                        {MOCK_BUILDINGS.map(b => {
                            const renderedRev = includeFuel ? b.total_value : b.revenue;
                            const renderedYield = includeFuel ? (b.total_value / b.total_sqft) : b.yield;
                            
                            return(
                            <div key={b.id} className="p-4 hover:bg-slate-50/50 transition-colors flex flex-col md:flex-row gap-6 md:items-center">
                                {/* Context */}
                                <div className="md:w-1/4">
                                    <div className="font-semibold text-slate-900">{b.name}</div>
                                    <div className="text-xs text-slate-500">{b.total_sqft.toLocaleString()} sqft</div>
                                </div>
                                
                                {/* Metrics Flex */}
                                <div className="md:w-3/4 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
                                    {/* Rev */}
                                    <div className="sm:w-1/3 space-y-2">
                                        <div className="flex justify-between items-end border-b pb-1">
                                            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{includeFuel ? 'Value' : 'Revenue'}</span>
                                            <span className="font-mono text-sm font-medium">{formatCur(renderedRev)}</span>
                                        </div>
                                        <div className="h-2 w-full">
                                            {renderSegments(b, 'rev')}
                                        </div>
                                    </div>
                                    
                                    {/* Occ */}
                                    <div className="sm:w-1/3 space-y-2">
                                        <div className="flex justify-between items-end border-b pb-1">
                                            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Occupied</span>
                                            <span className="font-mono text-sm font-medium">{b.occupancy_pct}%</span>
                                        </div>
                                        <div className="h-2 w-full">
                                            {renderSegments(b, 'occ')}
                                        </div>
                                    </div>

                                    {/* Yield */}
                                    <div className="sm:w-1/3 space-y-2">
                                        <div className="flex justify-between items-end border-b pb-1">
                                            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{includeFuel ? 'Value/SF' : 'Yield/SF'}</span>
                                            <div className="flex items-center gap-2">
                                                {renderedYield < b.market_avg && !includeFuel && (
                                                     <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200 px-1.5 py-0 h-4 text-[9px]">Under</Badge>
                                                )}
                                                <span className={`font-mono text-sm font-bold text-slate-900`}>{formatYld(renderedYield)}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="relative h-2 flex-1 bg-slate-100 rounded-sm">
                                                <div 
                                                    className={`h-full rounded-sm ${renderedYield < b.market_avg && !includeFuel ? 'bg-amber-400' : 'bg-emerald-400'}`} 
                                                    style={{ width: `${Math.min(100, (renderedYield / MAX_YIELD) * 100)}%` }}
                                                />
                                                <div 
                                                    className="absolute top-[-2px] bottom-[-2px] w-[2px] bg-slate-900"
                                                    style={{ left: `${(b.market_avg / MAX_YIELD) * 100}%` }}
                                                />
                                            </div>
                                            <span className="text-[10px] text-slate-400 font-mono w-8">avg{b.market_avg}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )})}
                    </Card>
                </div>
                
                {/* VARIATION 4: The Performance Matrix (Scatter Plot) */}
                <div className="space-y-4">
                    <div className="px-1">
                        <h2 className="text-xl font-bold tracking-tight text-slate-900">Variation 4: The Performance Matrix</h2>
                        <p className="text-sm text-slate-500">The "Consultant" view. Plots Occupancy vs Yield, with Revenue as bubble size. Instantly identifies underperformers vs stars.</p>
                    </div>
                    <Card className="p-6">
                        <div className="flex flex-col md:flex-row gap-8">
                            {/* The Chart Area */}
                            <div className="relative w-full md:w-2/3 aspect-square max-h-[450px] border-l-2 border-b-2 border-slate-300 bg-slate-50/50 rounded-tr-xl ml-4 mb-4">
                                {/* Quadrant Crosshairs */}
                                <div className="absolute top-0 bottom-0 left-[85%] border-l-2 border-dashed border-slate-300" />
                                <div className="absolute left-0 right-0 bottom-[66%] border-b-2 border-dashed border-slate-300" /> {/* Assuming $1.00 is target out of $1.50 Max */}
                                
                                {/* Axis Labels */}
                                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs font-semibold text-slate-500 tracking-wider uppercase">Occupancy % (Utilization) &rarr;</div>
                                <div className="absolute -left-10 top-1/2 -translate-y-1/2 -rotate-90 text-xs font-semibold text-slate-500 tracking-wider uppercase whitespace-nowrap">&larr; Yield / Sqft (Efficiency)</div>

                                {/* Crosshair Labels */}
                                <div className="absolute bottom-0 left-[85%] text-[10px] font-bold text-slate-400 -translate-x-1/2 translate-y-5">85% Target</div>
                                <div className="absolute left-0 bottom-[66%] text-[10px] font-bold text-slate-400 -translate-x-full pr-2 -translate-y-1/2 text-right">Avg<br/>$1.00</div>

                                {/* Axis Min/Max Labels */}
                                <div className="absolute -bottom-5 left-0 text-[10px] text-slate-400">0%</div>
                                <div className="absolute -bottom-5 right-0 text-[10px] text-slate-400">100%</div>
                                <div className="absolute -left-8 bottom-0 text-[10px] text-slate-400">$0</div>
                                <div className="absolute -left-10 top-0 text-[10px] text-slate-400">$1.50</div>

                                {/* Plot Points */}
                                {MOCK_BUILDINGS.map(b => {
                                    const renderedRev = includeFuel ? b.total_value : b.revenue;
                                    const renderedYield = includeFuel ? (b.total_value / b.total_sqft) : b.yield;
                                    const leftPct = b.occupancy_pct;
                                    const bottomPct = Math.min((renderedYield / MAX_YIELD) * 100, 100);
                                    // Bubble size between 50px and 140px based on revenue
                                    const size = Math.max(50, (renderedRev / MAX_REV) * 140);
                                    
                                    // Unique colors for the 3 distinct examples
                                    const theColor = b.id === 'hangar-a' 
                                        ? 'bg-amber-500/90 hover:bg-amber-500 shadow-amber-500/30' 
                                        : (b.id === 'hangar-b' 
                                            ? 'bg-blue-500/90 hover:bg-blue-500 shadow-blue-500/30' 
                                            : 'bg-emerald-500/90 hover:bg-emerald-500 shadow-emerald-500/30')

                                    return (
                                        <TooltipProvider key={b.id}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div 
                                                        className={`absolute rounded-full flex items-center justify-center text-white font-bold text-xs shadow-xl transform -translate-x-1/2 translate-y-1/2 cursor-pointer transition-all duration-300 hover:scale-105 ${theColor} border-2 border-white`}
                                                        style={{ 
                                                            left: `${leftPct}%`, 
                                                            bottom: `${bottomPct}%`,
                                                            width: size,
                                                            height: size
                                                        }}
                                                    >
                                                        <span className="drop-shadow-md text-center leading-tight">
                                                            {b.name.split(' ')[1] || b.name.charAt(0)}
                                                            <span className="block text-[9px] font-normal opacity-90">{formatCur(renderedRev)}</span>
                                                        </span>
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent className="p-4 bg-white border border-slate-200 shadow-2xl z-50 rounded-xl max-w-xs">
                                                    <div className="font-bold text-slate-900 text-lg mb-1">{b.name}</div>
                                                    <div className="text-xs text-slate-500 mb-4">{b.note}</div>
                                                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                                        <span className="text-slate-500">{includeFuel ? 'Total Value (Size)' : 'Revenue (Size)'}</span>
                                                        <span className="font-bold text-slate-900">{formatCur(renderedRev)}</span>
                                                        <span className="text-slate-500">Occupancy (X)</span>
                                                        <span className="font-bold text-slate-900">{b.occupancy_pct}%</span>
                                                        <span className="text-slate-500">{includeFuel ? 'Value / Sqft (Y)' : 'Yield / Sqft (Y)'}</span>
                                                        <span className="font-bold text-slate-900">{formatYld(renderedYield)}</span>
                                                    </div>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    )
                                })}
                            </div>

                            {/* Legend / Analysis text */}
                            <div className="w-full md:w-1/3 flex flex-col justify-center space-y-6 pt-4">
                                <div>
                                    <h3 className="font-bold text-slate-900 mb-3 text-lg">Portfolio Analysis</h3>
                                    <ul className="space-y-4 text-sm text-slate-600">
                                        <li className="flex gap-3">
                                            <div className="w-5 h-5 rounded-full bg-slate-200 border-2 border-slate-400 shrink-0" />
                                            <span><strong>Bubble Size</strong> = Total Revenue ($)</span>
                                        </li>
                                    </ul>
                                </div>
                                <div className="space-y-3">
                                    <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-lg text-sm shadow-sm">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <div className="w-3 h-3 rounded-full bg-amber-500" />
                                            <strong className="text-amber-900">High Occ. / Low Yield</strong>
                                        </div>
                                        <span className="text-amber-800">Hangar A has maxed out physical space but underpriced current leases. Time to push rates.</span>
                                    </div>
                                    <div className="p-3.5 bg-blue-50 border border-blue-200 rounded-lg text-sm shadow-sm">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <div className="w-3 h-3 rounded-full bg-blue-500" />
                                            <strong className="text-blue-900">Low Occ. / High Yield</strong>
                                        </div>
                                        <span className="text-blue-800">Hangar B is priced perfectly but needs aggressive leasing effort to fill empty space.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* VARIATION 5: The KPI Heatmap */}
                <div className="space-y-4">
                     <div className="px-1">
                        <h2 className="text-xl font-bold tracking-tight text-slate-900">Variation 5: The KPI Heatmap</h2>
                        <p className="text-sm text-slate-500">A high-density scorecard. Color intensity indicates performance against targets independent of absolute size.</p>
                    </div>
                    <Card className="overflow-hidden border-slate-300 shadow-sm">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-slate-900 text-slate-300 font-medium">
                                <tr>
                                    <th className="p-4">Facility Segment</th>
                                    <th className="p-4 text-right border-l border-slate-700">Monthly Revenue</th>
                                    <th className="p-4 text-right border-l border-slate-700 w-[20%]">Occupancy</th>
                                    <th className="p-4 text-right border-l border-slate-700 w-[20%]">Yield per Sqft</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {MOCK_BUILDINGS.map(b => {
                                    const renderedRev = includeFuel ? b.total_value : b.revenue;
                                    const renderedYield = includeFuel ? (b.total_value / b.total_sqft) : b.yield;
                                    // Heatmap logic
                                    const getOccColor = (occ: number) => {
                                        if (occ >= 90) return 'bg-emerald-100 text-emerald-900'
                                        if (occ >= 75) return 'bg-emerald-50 text-emerald-800'
                                        if (occ >= 60) return 'bg-amber-100 text-amber-900'
                                        return 'bg-rose-100 text-rose-900'
                                    }

                                    const getYieldColor = (yieldVal: number, mktAvg: number) => {
                                        const ratio = yieldVal / mktAvg
                                        if (ratio >= 1.05) return 'bg-emerald-100 text-emerald-900'
                                        if (ratio >= 0.95) return 'bg-slate-50 text-slate-800' // Expected
                                        if (ratio >= 0.80) return 'bg-amber-100 text-amber-900'
                                        return 'bg-rose-100 text-rose-900'
                                    }

                                    return (
                                        <tr key={b.id} className="group">
                                            <td className="p-4 font-medium text-slate-900 bg-white group-hover:bg-slate-50 transition-colors">
                                                <div className="text-base">{b.name}</div>
                                                <div className="text-xs text-slate-500 font-normal mt-0.5">{b.total_sqft.toLocaleString()} sqft total</div>
                                            </td>
                                            <td className="p-4 text-right border-l border-slate-100 bg-white group-hover:bg-slate-50 transition-colors">
                                                <div className="font-mono text-lg font-semibold text-slate-900">{formatCur(renderedRev)}</div>
                                            </td>
                                            <td className={`p-4 text-right border-l-2 border-white transition-colors ${getOccColor(b.occupancy_pct)}`}>
                                                <div className="font-mono text-lg font-bold">{b.occupancy_pct}%</div>
                                                <div className="text-[10px] opacity-70 mt-1 uppercase tracking-wider font-semibold">Target: 85%</div>
                                            </td>
                                            <td className={`p-4 text-right border-l-2 border-white transition-colors ${getYieldColor(renderedYield, b.market_avg)}`}>
                                                <div className="font-mono text-lg font-bold">{formatYld(renderedYield)}</div>
                                                <div className="text-[10px] opacity-70 mt-1 uppercase tracking-wider font-semibold">Mkt Avg: {formatYld(b.market_avg)}</div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </Card>
                </div>
                
                {/* VARIATION 6: Triple Sparkline Row */}
                <div className="space-y-4 pt-12 border-t border-slate-200">
                     <div className="px-1">
                        <h2 className="text-xl font-bold tracking-tight text-slate-900">Variation 6: Triple Sparkline Row (The GM View)</h2>
                        <p className="text-sm text-slate-500">Places the 3 core metrics perfectly side-by-side to expose pricing problems.</p>
                    </div>
                    
                    <Card className="divide-y divide-slate-100 p-2">
                        {/* Header Row */}
                        <div className="flex items-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50 rounded-t-lg">
                           <div className="w-[180px] shrink-0">Facility</div>
                           <div className="flex-1 grid grid-cols-3 gap-8">
                               <div>1. {includeFuel ? 'Total Value' : 'Space Revenue'}</div>
                               <div>2. Occupancy</div>
                               <div>3. {includeFuel ? 'Value Yield' : 'Space Yield'} / Sqft</div>
                           </div>
                        </div>
                        
                        {MOCK_BUILDINGS.map(b => {
                            const renderedRev = includeFuel ? b.total_value : b.revenue;
                            const renderedYield = includeFuel ? (b.total_value / b.total_sqft) : b.yield;
                            const isPricingProblem = b.occupancy_pct >= 80 && renderedYield < b.market_avg;

                            return(
                            <div key={b.id} className="p-4 hover:bg-slate-50/50 transition-colors flex items-center group relative">
                                
                                {/* Label Column */}
                                <div className="w-[180px] shrink-0 pr-4">
                                    <div className="font-semibold text-slate-900 truncate">{b.name}</div>
                                    <div className="text-[10px] text-slate-500 font-mono mt-0.5">{b.total_sqft.toLocaleString()} sf</div>
                                </div>
                                
                                {/* 3 Metric Columns */}
                                <div className="flex-1 grid grid-cols-3 gap-8 items-center">
                                    
                                    {/* 1. Revenue Column */}
                                    <div className="flex items-center gap-3">
                                        <div className="h-3 bg-slate-100 rounded-sm flex-1 flex overflow-hidden">
                                            {renderSegments(b, 'rev')}
                                        </div>
                                        <div className="w-16 text-right font-mono text-sm font-semibold text-slate-700">
                                            {formatCur(renderedRev)}
                                        </div>
                                    </div>

                                    {/* 2. Occupancy Column */}
                                    <div className="flex items-center gap-3 opacity-90 group-hover:opacity-100 transition-opacity">
                                        <div className="h-3 bg-slate-100 rounded-sm flex-1 flex overflow-hidden">
                                           {renderSegments(b, 'occ')}
                                        </div>
                                        <div className="w-12 text-right font-mono text-sm font-medium text-slate-600">
                                            {b.occupancy_pct}%
                                        </div>
                                    </div>

                                    {/* 3. Yield Column */}
                                    <div className="flex items-center gap-3 relative">
                                        <div className="h-3 bg-slate-100 rounded-sm flex-1 relative flex">
                                            <div 
                                                className={`h-full ${!includeFuel || (renderedYield - b.yield) <= 0 ? 'rounded-sm' : 'rounded-l-sm'} opacity-90 ${b.yield < b.market_avg ? 'bg-rose-400' : 'bg-emerald-400'}`} 
                                                style={{ width: `${Math.min(100, (b.yield / MAX_YIELD) * 100)}%` }}
                                                title={`Space Yield: ${formatYld(b.yield)}`}
                                            />
                                            {includeFuel && (renderedYield - b.yield) > 0 && (
                                                <div 
                                                    className="h-full bg-amber-400 opacity-90 rounded-r-sm border-l border-white/30" 
                                                    style={{ width: `${Math.min(100, ((renderedYield - b.yield) / MAX_YIELD) * 100)}%` }}
                                                    title={`Fuel Yield: ${formatYld(renderedYield - b.yield)}`}
                                                />
                                            )}
                                            {/* Target Line */}
                                            <div 
                                                className="absolute top-[-2px] bottom-[-2px] w-[2px] bg-slate-400 border-l border-white shadow-sm"
                                                style={{ left: `${(b.market_avg / MAX_YIELD) * 100}%` }}
                                            />
                                        </div>
                                        <div className="w-14 text-right font-mono text-sm font-bold text-slate-800 flex items-center justify-end gap-1.5 whitespace-nowrap">
                                            {formatYld(renderedYield)}
                                        </div>

                                        {/* Warning Indicator */}
                                        {isPricingProblem && !includeFuel && (
                                            <TooltipProvider delayDuration={0}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <div className="absolute -right-8 flex items-center justify-center cursor-help">
                                                            <div className="text-amber-500 text-base animate-pulse">⚠️</div>
                                                        </div>
                                                    </TooltipTrigger>
                                                    <TooltipContent className="bg-amber-50 border-amber-200 text-amber-900 shadow-md">
                                                        <p className="font-bold">Pricing Alert</p>
                                                        <p className="text-xs">High occupancy ({b.occupancy_pct}%) but below-market yield.</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        )}
                                    </div>
                                    
                                </div>
                            </div>
                        )})}
                    </Card>
                </div>
                
                <div className="h-16" />
            </div>
        </div>
    )
}
